import { jwtDecode } from "jwt-decode";

/**
 * Read a single boolean claim from the access token. Returns `undefined`
 * for unparseable tokens, missing claims, or non-boolean claim values.
 */
export function getJwtBoolClaim(
  token: string,
  claim: string,
): boolean | undefined {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    const value = decoded[claim];
    return typeof value === "boolean" ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read a list-of-ids claim from the access token, as strings. Returns
 * `undefined` for unparseable tokens, missing claims, or non-list values, so
 * "assigned nothing" (`[]`) stays distinct from "not said".
 */
export function getJwtIdListClaim(
  token: string,
  claim: string,
): string[] | undefined {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    const value = decoded[claim];
    return Array.isArray(value) ? value.map(String) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read a numeric claim from the access token. Returns `undefined` for
 * unparseable tokens, missing claims, or values that are not finite numbers.
 */
export function getJwtNumberClaim(
  token: string,
  claim: string,
): number | undefined {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    const value = decoded[claim];
    return typeof value === "number" && Number.isFinite(value)
      ? value
      : undefined;
  } catch {
    return undefined;
  }
}

/** The idle timeout when the token does not state one. */
export const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;

/** Minutes without input after which the console signs this session out. */
export function idleTimeoutMinutes(token: string): number {
  const minutes = getJwtNumberClaim(token, "idle_timeout_minutes");
  return minutes !== undefined && minutes > 0
    ? minutes
    : DEFAULT_IDLE_TIMEOUT_MINUTES;
}

/** When this session's original sign-in happened (epoch ms), if the token says. */
export function authTimeMs(token: string): number | undefined {
  const seconds = getJwtNumberClaim(token, "auth_time");
  return seconds !== undefined && seconds > 0 ? seconds * 1000 : undefined;
}

/** Did the user complete the MFA challenge? */
export function isMfaPassed(token: string): boolean {
  return getJwtBoolClaim(token, "mfa_passed") === true;
}

/** Is the user in the post-login pre-MFA state?
 *  True for enrolled users who haven't verified yet, AND for privileged
 *  unenrolled users whose role requires MFA (must enroll before proceeding),
 *  unless they chose "Skip for now" (`mfa_deferred`). A deferral never excuses
 *  an enrolled user: they have a device, so they verify. */
export function isMfaPending(token: string): boolean {
  if (getJwtBoolClaim(token, "mfa_passed") !== false) return false;
  // An impersonated session can never prove the customer's factor, and the MFA
  // pages refuse it; the administrator proved their own to start it.
  if (getJwtBoolClaim(token, "is_impersonated") === true) return false;
  if (getJwtBoolClaim(token, "mfa_enrolled") === true) return true;
  return (
    getJwtBoolClaim(token, "mfa_required") === true &&
    getJwtBoolClaim(token, "mfa_deferred") !== true
  );
}

/** Did the user put off enrolling? Drives the "set up two-factor" banner. */
export function isMfaDeferred(token: string): boolean {
  return (
    getJwtBoolClaim(token, "mfa_deferred") === true &&
    getJwtBoolClaim(token, "mfa_enrolled") !== true
  );
}

/** Does the user have a confirmed TOTP device? */
export function isMfaEnrolled(token: string): boolean {
  return getJwtBoolClaim(token, "mfa_enrolled") === true;
}

/** May this user step up with their password instead of a TOTP code?
 *  Mirrors `MfaDisableView`'s `totp_only` rule: a role that mandates MFA must
 *  prove possession, since letting the very factor MFA defends (a stolen
 *  password) remove it would defeat the protection.
 *
 *  Deliberately requires the claim to be explicitly `false` rather than
 *  negating `=== true`: a missing or unparseable claim degrades to TOTP-only,
 *  which every user can satisfy, instead of offering a field the API rejects. */
export function canStepUpWithPassword(token: string): boolean {
  return getJwtBoolClaim(token, "mfa_required") === false;
}

/** Is the user signed in with a confirmed device but no second factor
 *  presented this session? True for anyone signed in without being asked for a
 *  code: a single sign-on session whose identity provider asserted no MFA, or
 *  an exempt email domain. `mfa_passed` comes from policy there, so the
 *  endpoints that manage the device and its recovery codes answer
 *  `step_up_required` until the challenge is completed.
 *
 *  A missing `mfa_factor_presented` claim reads as not presented. Never true
 *  for an impersonated session, which cannot prove the customer's factor. */
export function needsFactorStepUp(token: string): boolean {
  return (
    isMfaPassed(token) &&
    isMfaEnrolled(token) &&
    getJwtBoolClaim(token, "mfa_factor_presented") !== true &&
    !isImpersonated(token)
  );
}

/** The MFA page this token belongs on while MFA is pending: the challenge
 *  page for an enrolled user, the enroll page otherwise. Returns null when
 *  MFA is not pending (already passed, not required, or unparseable token). */
export function mfaRedirectPath(token: string): string | null {
  if (!isMfaPending(token)) {
    return null;
  }
  return isMfaEnrolled(token) ? "/auth/mfa/challenge" : "/auth/mfa/enroll";
}

/** Is the user currently in an impersonation session? */
export function isImpersonated(token: string): boolean {
  return getJwtBoolClaim(token, "is_impersonated") === true;
}

/** Does the user have the can_impersonate flag set? */
export function canImpersonate(token: string): boolean {
  return getJwtBoolClaim(token, "can_impersonate") === true;
}

/**
 * The organizations restricted platform staff are assigned to, from the
 * token's `allowed_organization_ids`. `undefined` for an unrestricted platform
 * admin, for any role below the platform, and for a token that does not carry
 * the claim: unknown is not "nothing", so those keep the role's answer.
 */
export function platformAssignment(
  role: string | undefined,
  token: string | null | undefined,
): string[] | undefined {
  if (!token) return undefined;
  if (role === "platform_admin") {
    if (getJwtBoolClaim(token, "is_full_platform_admin") !== false) {
      return undefined;
    }
  } else if (role !== "platform_manager") {
    return undefined;
  }
  return getJwtIdListClaim(token, "allowed_organization_ids");
}
